import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './App.css';
import AESTest from './AESTest';
import NFTDisplay from './NFTDisplay';
import AESService from './services/aesService';
import AdminLogin from './components/AdminLogin';
import UserManagement from './components/UserManagement';
import UserLogin from './components/UserLogin';
import UserRegistration from './components/UserRegistration';
import LandingPage from './components/LandingPage';
import EncryptedDataViewer from './components/EncryptedDataViewer';
import { apiUrl } from './config/api';

function App() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [fabricStatus, setFabricStatus] = useState('checking');
  const [formData, setFormData] = useState({
    deviceId: '',
    type: 'sensor',
    manufacturer: '',
    registeredBy: '',
    publicKey: '',
    serialNumber: '',
    hardwareId: '',
    macAddress: '',
    model: ''
  });
  const [updateFormData, setUpdateFormData] = useState({
    deviceType: '',
    manufacturer: '',
    publicKey: ''
  });
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [currentView, setCurrentView] = useState('auth');
  const [user, setUser] = useState(null);
  const [authView, setAuthView] = useState('login');

  useEffect(() => {
    checkFabricStatus();
    loadDevices();
    checkAdminSession();
    checkUserSession();
  }, []);

  const checkAdminSession = () => {
    const token = localStorage.getItem('adminToken');
    const storedAdminUser = localStorage.getItem('adminUser');
    if (token && storedAdminUser) {
      try {
        const parsedAdminUser = JSON.parse(storedAdminUser);
        setAdminUser(parsedAdminUser);
        setCurrentView('devices');
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      } catch (sessionError) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
      }
    }
  };

  const checkUserSession = () => {
    const token = localStorage.getItem('userToken');
    const storedUser = localStorage.getItem('userData');
    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setCurrentView('devices');
        axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      } catch (sessionError) {
        localStorage.removeItem('userToken');
        localStorage.removeItem('userData');
      }
    }
  };

  const handleAdminLogin = (userData) => {
    setAdminUser(userData);
    setCurrentView('devices');
  };

  const handleAdminLogout = () => {
    setAdminUser(null);
    setCurrentView('auth');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    delete axios.defaults.headers.common.Authorization;
  };

  const handleUserLogin = (userData) => {
    setUser(userData);
    setCurrentView('devices');
  };

  const handleUserRegistration = (userData) => {
    setUser(userData);
    setCurrentView('devices');
  };

  const handleUserLogout = () => {
    setUser(null);
    setCurrentView('auth');
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    delete axios.defaults.headers.common.Authorization;
  };

  const checkFabricStatus = async () => {
    try {
      await axios.get(apiUrl('/devices'));
      setFabricStatus('fabric');
    } catch (requestError) {
      setFabricStatus('mock');
    }
  };

  const loadDevices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(apiUrl('/devices'));
      const decryptedDevices = await Promise.all(
        response.data.map(async (device) => {
          try {
            return await AESService.decryptDeviceData(device);
          } catch (decryptionError) {
            return device;
          }
        })
      );
      setDevices(decryptedDevices);
      setError('');
    } catch (requestError) {
      setError('Failed to load devices: ' + requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value
    });
  };

  const handleUpdateInputChange = (event) => {
    setUpdateFormData({
      ...updateFormData,
      [event.target.name]: event.target.value
    });
  };

  const registerDevice = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const encryptedData = await AESService.encryptDeviceData(formData);
      await axios.post(apiUrl('/devices/register'), encryptedData);
      setMessage('Device registered successfully with automatic encryption!');
      setFormData({
        deviceId: '',
        type: 'sensor',
        manufacturer: '',
        registeredBy: '',
        publicKey: '',
        serialNumber: '',
        hardwareId: '',
        macAddress: '',
        model: ''
      });
      loadDevices();
      setError('');
    } catch (requestError) {
      setError('Failed to register device: ' + requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const getDevice = async (deviceId) => {
    try {
      const response = await axios.get(apiUrl(`/devices/${deviceId}`));
      const deviceObj = response.data.data || response.data;
      const decryptedDevice = await AESService.decryptDeviceData(deviceObj);
      setSelectedDevice(decryptedDevice);
      setError('');
    } catch (requestError) {
      setError('Failed to get device: ' + requestError.message);
    }
  };

  const updateDevice = async (deviceId) => {
    setLoading(true);
    try {
      const encryptedData = await AESService.encryptDeviceData(updateFormData);
      await axios.put(apiUrl(`/devices/${deviceId}`), encryptedData);
      setMessage('Device updated successfully with automatic encryption!');
      setUpdateFormData({ deviceType: '', manufacturer: '', publicKey: '' });
      loadDevices();
      setError('');
    } catch (requestError) {
      setError('Failed to update device: ' + requestError.message);
    } finally {
      setLoading(false);
    }
  };

  const activateDevice = async (deviceId) => {
    try {
      await axios.post(apiUrl(`/devices/${deviceId}/activate`));
      setMessage('Device activated successfully!');
      loadDevices();
      setError('');
    } catch (requestError) {
      setError('Failed to activate device: ' + requestError.message);
    }
  };

  const deactivateDevice = async (deviceId) => {
    try {
      await axios.post(apiUrl(`/devices/${deviceId}/deactivate`));
      setMessage('Device deactivated successfully!');
      loadDevices();
      setError('');
    } catch (requestError) {
      setError('Failed to deactivate device: ' + requestError.message);
    }
  };

  const deleteDevice = async (deviceId) => {
    if (!window.confirm('Are you sure you want to delete this device?')) {
      return;
    }

    try {
      await axios.delete(apiUrl(`/devices/${deviceId}`));
      setMessage('Device deleted successfully!');
      loadDevices();
      setError('');
    } catch (requestError) {
      setError('Failed to delete device: ' + requestError.message);
    }
  };

  const clearMessages = () => {
    setMessage('');
    setError('');
  };

  if (currentView === 'auth') {
    return <LandingPage onUserLogin={() => setCurrentView('user-auth')} onAdminLogin={() => setCurrentView('admin-login')} />;
  }

  if (currentView === 'user-auth') {
    if (authView === 'register') {
      return <UserRegistration onRegistrationSuccess={handleUserRegistration} onSwitchToLogin={() => setAuthView('login')} onBack={() => setCurrentView('auth')} />;
    }
    return <UserLogin onLoginSuccess={handleUserLogin} onSwitchToRegistration={() => setAuthView('register')} onBack={() => setCurrentView('auth')} />;
  }

  if (currentView === 'admin-login') {
    return <AdminLogin onLoginSuccess={handleAdminLogin} onBack={() => setCurrentView('auth')} />;
  }

  if (currentView === 'user-management' && adminUser) {
    return (
      <div className="App">
        <div className="header">
          <h1>IoT Device Registry</h1>
          <p>Hyperledger Fabric Blockchain Management</p>
          <div className="admin-nav">
            <button onClick={() => setCurrentView('devices')} className="nav-btn">Devices</button>
            <button onClick={() => setCurrentView('user-management')} className="nav-btn active">Users</button>
            <button onClick={() => setCurrentView('encrypted-data')} className="nav-btn">Encrypted Data</button>
            <button onClick={handleAdminLogout} className="nav-btn logout">Logout ({adminUser.username})</button>
          </div>
        </div>
        <UserManagement />
      </div>
    );
  }

  if (currentView === 'encrypted-data' && adminUser) {
    return (
      <div className="App">
        <div className="header">
          <h1>IoT Device Registry</h1>
          <p>Hyperledger Fabric Blockchain Management</p>
          <div className="admin-nav">
            <button onClick={() => setCurrentView('devices')} className="nav-btn">Devices</button>
            <button onClick={() => setCurrentView('user-management')} className="nav-btn">Users</button>
            <button onClick={() => setCurrentView('encrypted-data')} className="nav-btn active">Encrypted Data</button>
            <button onClick={handleAdminLogout} className="nav-btn logout">Logout ({adminUser.username})</button>
          </div>
        </div>
        <EncryptedDataViewer />
      </div>
    );
  }

  return (
    <div className="App">
      <div className="header">
        <h1>IoT Device Registry</h1>
        <p>Hyperledger Fabric Blockchain Management</p>
        <div className="admin-nav">
          {adminUser ? (
            <>
              <button onClick={() => setCurrentView('devices')} className="nav-btn active">Devices</button>
              <button onClick={() => setCurrentView('user-management')} className="nav-btn">Users</button>
              <button onClick={() => setCurrentView('encrypted-data')} className="nav-btn">Encrypted Data</button>
              <button onClick={handleAdminLogout} className="nav-btn logout">Logout ({adminUser.username})</button>
            </>
          ) : user ? (
            <>
              <button onClick={() => setCurrentView('devices')} className="nav-btn active">Devices</button>
              <button onClick={handleUserLogout} className="nav-btn logout">Logout ({user.username})</button>
            </>
          ) : (
            <button onClick={() => setCurrentView('auth')} className="nav-btn home">Home</button>
          )}
        </div>
      </div>

      <div className="container">
        {fabricStatus === 'fabric' && <div className="fabric-status">Connected to Hyperledger Fabric Network</div>}
        {fabricStatus === 'mock' && <div className="mock-status">Running in Mock Mode (Fabric connection failed)</div>}

        <div className="encryption-banner">
          <strong>Automatic AES Encryption Enabled</strong>
          <small>Sensitive fields are automatically encrypted before storage and decrypted for display.</small>
        </div>

        {message && (
          <div className="alert alert-success">
            {message}
            <button onClick={clearMessages}>x</button>
          </div>
        )}
        {error && (
          <div className="alert alert-error">
            {error}
            <button onClick={clearMessages}>x</button>
          </div>
        )}

        <div className="card">
          <h2>Register New Device</h2>
          <form onSubmit={registerDevice}>
            <div className="form-group">
              <label>Device ID:</label>
              <input type="text" name="deviceId" value={formData.deviceId} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>Device Type:</label>
              <select name="type" value={formData.type} onChange={handleInputChange}>
                <option value="sensor">Sensor</option>
                <option value="actuator">Actuator</option>
                <option value="gateway">Gateway</option>
                <option value="controller">Controller</option>
              </select>
            </div>
            <div className="form-group">
              <label>Manufacturer:</label>
              <input type="text" name="manufacturer" value={formData.manufacturer} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>Registered By:</label>
              <input type="text" name="registeredBy" value={formData.registeredBy} onChange={handleInputChange} required />
            </div>
            <div className="form-group">
              <label>Public Key:</label>
              <input type="text" name="publicKey" value={formData.publicKey} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Serial Number:</label>
              <input type="text" name="serialNumber" value={formData.serialNumber} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Hardware ID:</label>
              <input type="text" name="hardwareId" value={formData.hardwareId} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>MAC Address:</label>
              <input type="text" name="macAddress" value={formData.macAddress} onChange={handleInputChange} />
            </div>
            <div className="form-group">
              <label>Model:</label>
              <input type="text" name="model" value={formData.model} onChange={handleInputChange} />
            </div>
            <button type="submit" className="btn" disabled={loading}>{loading ? 'Registering...' : 'Register Device'}</button>
          </form>
        </div>

        <div className="card">
          <h2>Update Device</h2>
          <div className="form-group">
            <label>Device ID to Update:</label>
            <input type="text" placeholder="Enter device ID" onChange={(event) => setSelectedDevice({ deviceId: event.target.value })} />
          </div>
          <div className="form-group">
            <label>New Device Type:</label>
            <input type="text" name="deviceType" value={updateFormData.deviceType} onChange={handleUpdateInputChange} />
          </div>
          <div className="form-group">
            <label>Manufacturer:</label>
            <input type="text" name="manufacturer" value={updateFormData.manufacturer} onChange={handleUpdateInputChange} />
          </div>
          <div className="form-group">
            <label>Public Key:</label>
            <input type="text" name="publicKey" value={updateFormData.publicKey} onChange={handleUpdateInputChange} />
          </div>
          <button onClick={() => updateDevice(selectedDevice?.deviceId)} className="btn" disabled={!selectedDevice?.deviceId || loading}>
            {loading ? 'Updating...' : 'Update Device'}
          </button>
        </div>

        <div className="card">
          <h2>Registered Devices ({devices.length})</h2>
          <button onClick={loadDevices} className="btn" disabled={loading}>{loading ? 'Loading...' : 'Refresh Devices'}</button>

          {loading ? (
            <div className="loading">Loading devices...</div>
          ) : devices.length === 0 ? (
            <div className="loading">No devices registered yet.</div>
          ) : (
            <div className="device-list">
              {devices.map((device) => (
                <div key={device.deviceId} className="device-card">
                  <h3>{device.deviceId}</h3>
                  <div className="device-info"><strong>Status:</strong> <span className={device.status === 'active' ? 'status-active' : 'status-inactive'}>{device.status}</span></div>
                  <div className="device-info"><strong>Timestamp:</strong> {new Date(device.timestamp).toLocaleString()}</div>
                  {device.type && <div className="device-info"><strong>Type:</strong> {device.type}</div>}
                  {device.manufacturer && <div className="device-info"><strong>Manufacturer:</strong> {device.manufacturer}</div>}
                  {device.registeredBy && <div className="device-info"><strong>Registered By:</strong> {device.registeredBy}</div>}
                  <div className="device-actions">
                    <button onClick={() => getDevice(device.deviceId)} className="btn">View Details</button>
                    <button onClick={() => activateDevice(device.deviceId)} className="btn btn-success">Activate</button>
                    <button onClick={() => deactivateDevice(device.deviceId)} className="btn">Deactivate</button>
                    <button onClick={() => deleteDevice(device.deviceId)} className="btn btn-danger">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedDevice && selectedDevice.deviceId && (
          <div className="card">
            <h2>Device Details</h2>
            <pre className="device-json">{JSON.stringify(selectedDevice, null, 2)}</pre>
            <button onClick={() => setSelectedDevice(null)} className="btn">Close Details</button>
            <NFTDisplay deviceId={selectedDevice.deviceId} nftIdentity={selectedDevice.nftIdentity || {}} showMissingMessage={!selectedDevice.nftIdentity} />
          </div>
        )}

        <AESTest />
      </div>
    </div>
  );
}

export default App;
